/**
 * Alle Zeilen einer Abfrage, Seite fuer Seite.
 *
 * PostgREST liefert je Anfrage hoechstens 1000 Zeilen (Supabase „Max rows“)
 * und meldet das nicht: ohne `.range()` fehlt alles dahinter still. Vorfall
 * 23.10.2026 — der Sendeverlauf von 6254 zeigte 2 von 24 Eintraegen, die
 * Kundenliste 1000 von 1244 Kunden, das API-Protokoll rechnete seine Kosten
 * ueber 1000 von 11566 Aufrufen.
 *
 * `seite(von, bis)` baut die Abfrage fuer JEDE Seite neu und haengt
 * `.range(von, bis)` an — ein Query-Builder laesst sich nur einmal abschicken.
 * Die Sortierung muss eindeutig sein (zuletzt nach `id`): sonst erscheinen
 * Zeilen beim Blaettern doppelt oder fallen ganz aus.
 *
 * Ein Fehler wird unveraendert geworfen. Eine leere Liste hiesse sonst „es
 * gibt nichts“, wo in Wahrheit nichts geladen wurde.
 *
 * Doppelte Zeilen (gleiche `id`) werden verworfen. Entsteht zwischen zwei
 * Seiten eine neue Zeile vor dem Offset — bei „neueste zuerst“ jeder neue
 * Logeintrag —, rutscht alles eine Position nach hinten, und die letzte
 * Zeile der vorigen Seite kaeme ein zweites Mal: das API-Protokoll zaehlte
 * den Aufruf doppelt (Codex-Review 23.10.2026). Dafuer muss der Select die
 * `id` enthalten. Bleibt die Gegenrichtung: eine zwischen zwei Seiten
 * GELOESCHTE Zeile vor dem Offset laesst eine andere ausfallen, und eine
 * Zeile, deren Sortierschluessel sich dazwischen aendert und die dadurch nach
 * vorn rueckt (ein bearbeiteter Lead bei `updated_at desc`), fehlt ganz. Das
 * Zeitfenster sind Millisekunden; wer es nicht hinnehmen kann, braucht
 * Keyset-Paging statt Offsets.
 */
export const ZEILEN_JE_SEITE = 1000;

const zeilenId = (zeile: unknown): string | number | undefined => {
    if (typeof zeile !== 'object' || zeile === null) return undefined;
    const id = (zeile as { id?: unknown }).id;
    return typeof id === 'string' || typeof id === 'number' ? id : undefined;
};

export const ladeAlleSeiten = async <T>(
    seite: (von: number, bis: number) => PromiseLike<{ data: T[] | null; error: unknown }>,
): Promise<T[]> => {
    const alle: T[] = [];
    const gesehen = new Set<string | number>();
    for (let von = 0; ; von += ZEILEN_JE_SEITE) {
        const { data, error } = await seite(von, von + ZEILEN_JE_SEITE - 1);
        if (error) throw error;
        const zeilen = data ?? [];
        for (const zeile of zeilen) {
            const id = zeilenId(zeile);
            if (id !== undefined) {
                if (gesehen.has(id)) continue;
                gesehen.add(id);
            }
            alle.push(zeile);
        }
        // Die Abbruchbedingung zaehlt die GELIEFERTEN Zeilen, nicht die
        // uebernommenen — eine volle Seite mit einer Dublette ist trotzdem voll.
        if (zeilen.length < ZEILEN_JE_SEITE) return alle;
    }
};
